/**
 * imageEffects.ts, Canvas-based post-capture processing pipeline
 * Applied after every photo capture before saving to server.
 */

import type { EffectsConfig } from '@/types'

/** Convert a dataURL to an HTMLImageElement */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

/**
 * Apply beauty filter: subtle gaussian blur + brightness boost using CSS filter trick.
 * This uses an offscreen canvas with SVG feGaussianBlur for a skin-smoothing look.
 */
function applyBeautyFilter(ctx: CanvasRenderingContext2D, w: number, h: number, strength: number) {
  if (strength <= 0) return

  // 1. Create a copy of the canvas for the blurred layer
  const blurCanvas = document.createElement('canvas')
  blurCanvas.width = w
  blurCanvas.height = h
  const blurCtx = blurCanvas.getContext('2d')!
  blurCtx.drawImage(ctx.canvas, 0, 0)

  // 2. Apply blur to the copy
  const blurPx = Math.round(strength * 6)
  blurCtx.filter = `blur(${blurPx}px)`
  blurCtx.drawImage(blurCanvas, 0, 0)

  // 3. Blend the blurred copy back with 'soft-light' to smooth skin without losing sharp edges
  ctx.save()
  ctx.globalCompositeOperation = 'soft-light'
  ctx.globalAlpha = strength * 0.7
  ctx.drawImage(blurCanvas, 0, 0)
  ctx.restore()

  // 4. Very mild brightness boost (much less aggressive than before)
  const imageData = ctx.getImageData(0, 0, w, h)
  const data = imageData.data
  const boost = 1 + strength * 0.03
  for (let i = 0; i < data.length; i += 4) {
    data[i] = Math.min(255, data[i] * boost)
    data[i + 1] = Math.min(255, data[i + 1] * boost)
    data[i + 2] = Math.min(255, data[i + 2] * boost)
  }
  ctx.putImageData(imageData, 0, 0)
}

/**
 * Apply green screen (chroma key): remove green pixels, replace with background image.
 */
async function applyGreenScreen(ctx: CanvasRenderingContext2D, w: number, h: number, config: EffectsConfig['greenScreen']) {
  const imageData = ctx.getImageData(0, 0, w, h)
  const data = imageData.data
  const threshold = config.threshold

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2]
    // Green pixel: green channel dominant over red and blue
    if (g > threshold && g > r * 1.4 && g > b * 1.2) {
      data[i + 3] = 0 // make transparent
    }
  }
  ctx.putImageData(imageData, 0, 0)

  if (config.backgroundAsset) {
    try {
      const bgImg = await loadImage(config.backgroundAsset)
      const offscreen = document.createElement('canvas')
      offscreen.width = w
      offscreen.height = h
      const offCtx = offscreen.getContext('2d')!
      offCtx.drawImage(bgImg, 0, 0, w, h)
      offCtx.drawImage(ctx.canvas, 0, 0)
      ctx.clearRect(0, 0, w, h)
      ctx.drawImage(offscreen, 0, 0)
    } catch {
      // bg image failed to load, leave transparent areas
    }
  }
}

/**
 * Apply watermark: draw watermark image at configured position with opacity.
 */
async function applyWatermark(ctx: CanvasRenderingContext2D, w: number, h: number, config: EffectsConfig['watermark']) {
  if (!config.asset) return
  try {
    const img = await loadImage(config.asset)
    const margin = 20
    const maxW = w * 0.25
    const ratio = img.height / img.width
    const wmarkW = Math.min(img.width, maxW)
    const wmarkH = wmarkW * ratio

    let x = margin, y = margin
    const pos = config.position
    if (pos.includes('right')) x = w - wmarkW - margin
    if (pos.includes('center') && !pos.includes('top') && !pos.includes('bottom')) x = (w - wmarkW) / 2
    if (pos.includes('bottom')) y = h - wmarkH - margin
    if (pos.includes('center') && !pos.includes('left') && !pos.includes('right')) y = (h - wmarkH) / 2

    ctx.save()
    ctx.globalAlpha = config.opacity
    ctx.drawImage(img, x, y, wmarkW, wmarkH)
    ctx.restore()
  } catch {
    // watermark failed to load, skip
  }
}

/**
 * Main pipeline: apply all enabled effects to a dataURL photo and return the result.
 */
export async function applyEffects(dataUrl: string, effects: EffectsConfig): Promise<string> {
  if (!effects) return dataUrl

  const img = await loadImage(dataUrl)
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0)

  const w = canvas.width
  const h = canvas.height

  // 1. Green screen (must go first, changes pixel data)
  if (effects.greenScreen?.enabled) {
    await applyGreenScreen(ctx, w, h, effects.greenScreen)
  }

  // 2. Beauty filter
  if (effects.beautyFilter?.enabled) {
    applyBeautyFilter(ctx, w, h, effects.beautyFilter.strength)
  }

  // 3. Watermark (last, always on top)
  if (effects.watermark?.enabled) {
    await applyWatermark(ctx, w, h, effects.watermark)
  }

  // Green screen with no replacement background leaves real transparent pixels, // those must stay PNG or the transparency is lost. Everything else can be JPEG.
  const needsTransparency = !!effects.greenScreen?.enabled && !effects.greenScreen?.backgroundAsset
  return needsTransparency ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.92)
}
